/**
 * Fantasy Console 0 - Sound Editor Tab
 *
 * Main tab component for editing SFX. Integrates all sub-components
 * with state management, audio preview, and keyboard shortcuts.
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import type { SFX } from '@fc0/types'
import {
  SFX_COUNT,
  NOTES_PER_SFX,
  decodeBase64,
  encodeBase64,
} from '@fc0/types'
import { AudioEngine, decodeSFX, encodeSFX } from '@fc0/console'
import { useCart } from '../../context/CartContext'
import { usePersistedTabState } from '../../context/EditorTabStateContext'
import { useEditorHistory } from '../../hooks/useEditorHistory'
import { cloneSFX, cloneSFXBank, sfxHasContent } from '../../utils/soundUtils'
import {
  SFXSelector,
  PianoRoll,
  WaveformSelector,
  EffectSelector,
  SFXSettings,
  PlaybackControls,
  SFXActions,
  SelectionToolbar,
} from '../SoundEditor'
import styles from './SoundEditorTab.module.css'
import editorStyles from '../../styles/editor.module.css'

export function SoundEditorTab() {
  const { cart, updateSfx, cartLoadVersion } = useCart()

  // Persisted state
  const [selectedSfxIndex, setSelectedSfxIndex] = usePersistedTabState(
    'sound:selectedSfxIndex',
    0,
  )
  const [selectedWaveform, setSelectedWaveform] = usePersistedTabState(
    'sound:selectedWaveform',
    0,
  )
  const [selectedEffect, setSelectedEffect] = usePersistedTabState(
    'sound:selectedEffect',
    0,
  )
  const [clipboard, setClipboard] = usePersistedTabState<SFX | null>(
    'sound:clipboard',
    null,
  )

  // Non-persisted state
  const [isPlaying, setIsPlaying] = useState(false)
  const [playheadPosition, setPlayheadPosition] = useState(-1)
  const [noteStartTime, setNoteStartTime] = useState(0)
  const [selectedColumns, setSelectedColumns] = useState<Set<number>>(new Set())

  // Audio engine ref (lazily created)
  const audioEngineRef = useRef<AudioEngine | null>(null)
  const playIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Decode SFX bank from cart
  const sfxBank = useMemo(() => decodeSFX(cart.sfx), [cart.sfx])
  const currentSfx = sfxBank[selectedSfxIndex]

  // Undo/redo history (stores binary snapshots of the full SFX bank)
  const {
    pushState: pushSfxState,
    undo,
    redo,
    resetHistory,
  } = useEditorHistory((data) => updateSfx(decodeSFX(encodeBase64(data))))

  // Reset history when a new cart is loaded
  useEffect(() => {
    resetHistory(decodeBase64(cart.sfx ?? ''))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartLoadVersion, resetHistory])

  // Lazy audio engine getter
  const getAudioEngine = useCallback(() => {
    if (!audioEngineRef.current) {
      audioEngineRef.current = new AudioEngine()
    }
    return audioEngineRef.current
  }, [])

  // Stop playback cleanup
  const stopPlayback = useCallback(() => {
    if (playIntervalRef.current) {
      clearInterval(playIntervalRef.current)
      playIntervalRef.current = null
    }
    if (audioEngineRef.current) {
      audioEngineRef.current.stopAllChannelsFade(50)
    }
    setIsPlaying(false)
    setPlayheadPosition(-1)
  }, [])

  // Play SFX preview
  const playPreview = useCallback(
    (fromLoopStart: boolean = false) => {
      const sfx = sfxBank[selectedSfxIndex]
      const engine = getAudioEngine()

      engine.unlockAudio()
      engine.loadSFX(encodeSFX(sfxBank))

      const startOffset =
        fromLoopStart && sfx.loop_start < sfx.loop_end ? sfx.loop_start : 0
      engine.playSFX(selectedSfxIndex, 0, startOffset)

      setIsPlaying(true)
      setPlayheadPosition(startOffset)
      setNoteStartTime(performance.now())

      const msPerNote = (sfx.speed / 60) * 1000
      let noteIndex = startOffset

      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current)
      }

      playIntervalRef.current = setInterval(() => {
        noteIndex++

        // Handle looping
        if (sfx.loop_start < sfx.loop_end && noteIndex > sfx.loop_end) {
          noteIndex = sfx.loop_start
        }

        if (noteIndex >= NOTES_PER_SFX) {
          stopPlayback()
          return
        }

        // Early stop if remaining notes are all silent (non-looping)
        if (sfx.loop_start >= sfx.loop_end) {
          let hasRemaining = false
          for (let i = noteIndex; i < NOTES_PER_SFX; i++) {
            if (sfx.notes[i].volume > 0) {
              hasRemaining = true
              break
            }
          }
          if (!hasRemaining) {
            stopPlayback()
            return
          }
        }

        setPlayheadPosition(noteIndex)
        setNoteStartTime(performance.now())
      }, msPerNote)
    },
    [sfxBank, selectedSfxIndex, stopPlayback, getAudioEngine],
  )

  // Note preview callback for PianoRoll
  const handleNotePreview = useCallback(
    (pitch: number, waveform: number, volume: number) => {
      const engine = getAudioEngine()
      engine.unlockAudio()
      engine.previewNote(pitch, waveform, 80, volume)
    },
    [getAudioEngine],
  )

  // Update a single SFX in the bank (goes through undo/redo history)
  const handleSfxChange = useCallback(
    (newSfx: SFX) => {
      const newBank = cloneSFXBank(sfxBank)
      newBank[selectedSfxIndex] = newSfx
      pushSfxState(decodeBase64(encodeSFX(newBank)))
    },
    [sfxBank, selectedSfxIndex, pushSfxState],
  )

  // Settings handler - updates multiple SFX properties at once to avoid stale closure issues
  const handleSfxPropsChange = useCallback(
    (props: Partial<Pick<SFX, 'speed' | 'loop_start' | 'loop_end'>>) => {
      const newSfx = cloneSFX(currentSfx)
      if (props.speed !== undefined) newSfx.speed = props.speed
      if (props.loop_start !== undefined) newSfx.loop_start = props.loop_start
      if (props.loop_end !== undefined) newSfx.loop_end = props.loop_end
      handleSfxChange(newSfx)
    },
    [currentSfx, handleSfxChange],
  )

  // Clipboard handlers
  const handleCopy = useCallback(() => {
    setClipboard(cloneSFX(currentSfx))
  }, [currentSfx])

  const handlePaste = useCallback(() => {
    if (!clipboard) return
    handleSfxChange(cloneSFX(clipboard))
  }, [clipboard, handleSfxChange])

  const handleClear = useCallback(() => {
    const newSfx = cloneSFX(currentSfx)
    newSfx.speed = 1
    newSfx.notes.forEach((note) => {
      note.pitch = 0
      note.waveform = 0
      note.volume = 0
      note.effect = 0
    })
    handleSfxChange(newSfx)
  }, [currentSfx, handleSfxChange])

  // Selection operation handlers
  const handleTranspose = useCallback(
    (semitones: number) => {
      if (selectedColumns.size === 0) return
      const newSfx = cloneSFX(currentSfx)
      for (const col of selectedColumns) {
        const note = newSfx.notes[col]
        if (note.volume > 0) {
          note.pitch = Math.max(0, Math.min(63, note.pitch + semitones))
        }
      }
      handleSfxChange(newSfx)
    },
    [selectedColumns, currentSfx, handleSfxChange],
  )

  const handleSetWaveform = useCallback(
    (waveform: number) => {
      if (selectedColumns.size === 0) return
      const newSfx = cloneSFX(currentSfx)
      for (const col of selectedColumns) {
        const note = newSfx.notes[col]
        if (note.volume > 0) {
          note.waveform = waveform
        }
      }
      handleSfxChange(newSfx)
    },
    [selectedColumns, currentSfx, handleSfxChange],
  )

  const handleSetEffect = useCallback(
    (effect: number) => {
      if (selectedColumns.size === 0) return
      const newSfx = cloneSFX(currentSfx)
      for (const col of selectedColumns) {
        const note = newSfx.notes[col]
        if (note.volume > 0) {
          note.effect = effect
        }
      }
      handleSfxChange(newSfx)
    },
    [selectedColumns, currentSfx, handleSfxChange],
  )

  const handleDeleteSelected = useCallback(() => {
    if (selectedColumns.size === 0) return
    const newSfx = cloneSFX(currentSfx)
    for (const col of selectedColumns) {
      newSfx.notes[col].volume = 0
    }
    handleSfxChange(newSfx)
    setSelectedColumns(new Set())
  }, [selectedColumns, currentSfx, handleSfxChange])

  // Stop playback on unmount
  useEffect(() => {
    return () => {
      stopPlayback()
    }
  }, [stopPlayback])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't capture if we're in an input field
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      )
        return

      // Space: play/stop toggle
      if (e.key === ' ') {
        e.preventDefault()
        if (isPlaying) {
          stopPlayback()
        } else {
          playPreview()
        }
        return
      }

      // [ / ]: previous/next SFX
      if (e.key === '[') {
        e.preventDefault()
        setSelectedSfxIndex((prev) => Math.max(0, prev - 1))
        stopPlayback()
        return
      }
      if (e.key === ']') {
        e.preventDefault()
        setSelectedSfxIndex((prev) => Math.min(SFX_COUNT - 1, prev + 1))
        stopPlayback()
        return
      }

      // 1-8 (no modifier): select waveform
      if (!e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
        const num = parseInt(e.key)
        if (num >= 1 && num <= 8) {
          e.preventDefault()
          setSelectedWaveform(num - 1)
          return
        }
      }

      // Ctrl/Cmd+A: select all columns
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault()
        const all = new Set<number>()
        for (let i = 0; i < NOTES_PER_SFX; i++) all.add(i)
        setSelectedColumns(all)
        return
      }

      // Ctrl/Cmd shortcuts
      if (e.ctrlKey || e.metaKey) {
        // Undo
        if (e.key === 'z' && !e.shiftKey) {
          e.preventDefault()
          undo()
          return
        }
        // Redo (Ctrl+Shift+Z or Ctrl+Y)
        if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
          e.preventDefault()
          redo()
          return
        }
        if (e.key === 'c') {
          e.preventDefault()
          setClipboard(cloneSFX(sfxBank[selectedSfxIndex]))
          return
        }
        if (e.key === 'v') {
          e.preventDefault()
          if (clipboard) {
            const newBank = cloneSFXBank(sfxBank)
            newBank[selectedSfxIndex] = cloneSFX(clipboard)
            pushSfxState(decodeBase64(encodeSFX(newBank)))
          }
          return
        }
      }

      // Delete/Backspace: delete selected notes
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedColumns.size > 0) {
          e.preventDefault()
          handleDeleteSelected()
          return
        }
      }

      // Escape: clear selection
      if (e.key === 'Escape') {
        if (selectedColumns.size > 0) {
          e.preventDefault()
          setSelectedColumns(new Set())
          return
        }
      }

      // Up/Down: transpose selected
      if (!e.shiftKey && selectedColumns.size > 0) {
        if (e.key === 'ArrowUp') {
          e.preventDefault()
          handleTranspose(1)
          return
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          handleTranspose(-1)
          return
        }
      }

      // Shift+Up/Down: transpose selected by 12 semitones
      if (e.shiftKey && selectedColumns.size > 0) {
        if (e.key === 'ArrowUp') {
          e.preventDefault()
          handleTranspose(12)
          return
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          handleTranspose(-12)
          return
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [
    isPlaying,
    stopPlayback,
    playPreview,
    sfxBank,
    selectedSfxIndex,
    clipboard,
    pushSfxState,
    undo,
    redo,
    selectedColumns,
    handleDeleteSelected,
    handleTranspose,
  ])

  // Count non-empty SFX
  const usedCount = sfxBank.filter(sfxHasContent).length
  const hasLoop = currentSfx.loop_start < currentSfx.loop_end

  return (
    <div className={editorStyles.editorTabContainer}>
      <div className={editorStyles.editorArea}>
        <div className={styles.selectorPane}>
          <SFXSelector
            sfxBank={sfxBank}
            selectedIndex={selectedSfxIndex}
            onSelect={(i) => {
              setSelectedSfxIndex(i)
              stopPlayback()
              setSelectedColumns(new Set())
            }}
          />
        </div>
        <div className={styles.pianoRollPane}>
          <PianoRoll
            sfx={currentSfx}
            selectedWaveform={selectedWaveform}
            selectedEffect={selectedEffect}
            playheadPosition={playheadPosition}
            noteStartTime={noteStartTime}
            isPlaying={isPlaying}
            selectedColumns={selectedColumns}
            onSfxChange={handleSfxChange}
            onSfxPropsChange={handleSfxPropsChange}
            onNotePreview={handleNotePreview}
            onSelectionChange={setSelectedColumns}
          />
        </div>
      </div>

      {selectedColumns.size > 0 && (
        <div className={editorStyles.sectionRow}>
          <SelectionToolbar
            selectedCount={selectedColumns.size}
            onTranspose={handleTranspose}
            onSetWaveform={handleSetWaveform}
            onSetEffect={handleSetEffect}
            onDelete={handleDeleteSelected}
            onClearSelection={() => setSelectedColumns(new Set())}
          />
        </div>
      )}

      <div className={`${editorStyles.sectionRow} ${styles.controlsRow}`}>
        <WaveformSelector
          selected={selectedWaveform}
          onSelect={setSelectedWaveform}
        />
        <EffectSelector
          selected={selectedEffect}
          onSelect={setSelectedEffect}
        />
      </div>

      <div className={`${editorStyles.sectionRow} ${styles.settingsRow}`}>
        <PlaybackControls
          isPlaying={isPlaying}
          hasLoop={hasLoop}
          onPlay={() => playPreview(false)}
          onPlayLoop={() => playPreview(true)}
          onStop={stopPlayback}
        />
        <SFXSettings
          speed={currentSfx.speed}
          loopStart={currentSfx.loop_start}
          loopEnd={currentSfx.loop_end}
          onSfxPropsChange={handleSfxPropsChange}
        />
        <SFXActions
          hasClipboard={clipboard !== null}
          onCopy={handleCopy}
          onPaste={handlePaste}
          onClear={handleClear}
        />
      </div>
    </div>
  )
}
